import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SkeletonLoader, WrapperCardGrid, colors2 } from "oolib";
import { UI_CAPTION } from "oolib/dist/v2/components/Typo2";
import { resolveTopicClusteringLens } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getRoutePathToAiChat } from "@/utils/routePathGenerators";
import { useGetAnnotationExplorerData } from "@/components/analysisModules/AnnotationExplorer/useGetAnnotationExplorerData";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import { SignalCategoriesProvider, useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { SidePanelStack } from "@/components/UI/SidePanelStack";
import { DashboardHalves as Halves } from "@/_self_managed/SM_Dashboard/comps/DashboardHalves";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { isSinglePeriod, usePrioritiesScoring } from "./usePrioritiesScoring";
import { usePeriodSelection } from "./periodSelection";
import { SignalCategoryCards } from "./SignalCategoryCards";
import { SignalCategoryTrend } from "./SignalCategoryTrend";
import { TopicSharesWidget } from "./TopicSharesWidget";
import { ZoneSubtopicsWidget } from "./ZoneSubtopicsWidget";
import { ZonesGridWidget } from "./ZonesGridWidget";
import { TopicDrilldownPanel } from "./TopicDrilldownPanel";
import { CategoryTopicsPanel } from "./CategoryTopicsPanel";
import { SubtopicRowPanel } from "./SubtopicRowPanel";
import { activeFiltersFor, signalsFilter, signalIdsInCategory } from "./scopeFilters";
import { CalendarPlus } from "@phosphor-icons/react";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";

/**
 * The Priorities overview for a surface with no Topics sidebar (the
 * Dashboard's New tab): the signal category cards on the left and the
 * topics list on the right, half the width each, then the signal
 * categories' lines over time with the zones grid beside them, then the
 * Fires and Emerging sub-topics side by side. Resolves the scope
 * itself: the tenant's lens, the whole corpus (no filters), the latest
 * month on offer as the period, and the signal meta + category theme from
 * the counts response. Runs the same usePrioritiesScoring the Priorities
 * view runs, so landing on both costs the requests once.
 */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;
// The shared grid reserves empty columns (auto-fill) — fine for a page of
// topics, wrong for four cards that should share the whole row. auto-fit
// collapses the empty tracks so the cards stretch across.
const FILL_ROW = { gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" };
// The two widgets that read the signal series, one per row. Their own
// skeletons because each is also shown alone, while the rest of the section
// is already painted and only the signal series is still in flight.
const SignalCardsSkeleton = () => (
  <WrapperCardGrid minWidth="260px" style={FILL_ROW}>
    {[0, 1, 2, 3].map((i) => (
      <SkeletonLoader key={i} style={{ height: "14rem", borderRadius: 12 }} />
    ))}
  </WrapperCardGrid>
);
const SignalTrendSkeleton = () => <SkeletonLoader style={{ height: "100%", minHeight: "28rem", borderRadius: 12 }} />;
const SectionSkeleton = () => (
  <Stack>
    <Halves>
      <SignalCardsSkeleton />
      <SkeletonLoader style={{ height: "28rem", borderRadius: 12 }} />
    </Halves>
    <Halves>
      <SignalTrendSkeleton />
      <SkeletonLoader style={{ height: "28rem", borderRadius: 12 }} />
    </Halves>
    <Halves>
      <SkeletonLoader style={{ height: "22rem", borderRadius: 12 }} />
      <SkeletonLoader style={{ height: "22rem", borderRadius: 12 }} />
    </Halves>
  </Stack>
);

export const PrioritiesOverviewSection = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const topicClusteringLens = resolveTopicClusteringLens({ platformConfigs_ai: platformConfigs?.ai });

  // The shared period selection (the period, and what it is compared
  // with). `period` is null while the options load AND when the tenant has
  // no dated observation yet; `periodLoading` tells the two apart.
  const { period, comparePeriod, compareKey, periodLoading, dateFieldMap } = usePeriodSelection();

  // "Ask AI about this" from the topic panel's sub-topic level — the chat
  // pre-anchored to that node, as on the Topics page.
  const navigate = useNavigate();
  const askAiAbout = useCallback(
    (node) => {
      if (!node?.nodeId || node.isUnplaced || node.parkedOf) return;
      navigate(
        getRoutePathToAiChat({
          SELF_MANAGED_BASE_CONFIGS: platformConfigs?.SELF_MANAGED_BASE_CONFIGS,
          anchor: { nodeId: String(node.nodeId), lens: topicClusteringLens, kind: "overview", title: node.label },
        })
      );
    },
    [navigate, platformConfigs, topicClusteringLens]
  );

  const { tplOptions } = useGetAnnotationExplorerData({ dataSource: "annotations" });
  const titleByContentType = useMemo(
    () => Object.fromEntries((tplOptions || []).map((t) => [t.kp_content_type, t.general?.content?.title || t.kp_content_type])),
    [tplOptions]
  );

  const [signalMetaById, setSignalMetaById] = useState({});
  const [signalConfig, setSignalConfig] = useState({ signalCategories: null, prioritiesCopy: null });
  const rememberCountsMeta = useCallback((payload) => {
    if (payload?.signalMetaById) setSignalMetaById(payload.signalMetaById);
    if (payload?.signalCategories) setSignalConfig({ signalCategories: payload.signalCategories, prioritiesCopy: payload.prioritiesCopy || null });
  }, []);
  const theme = useMemo(
    () => buildSignalCategoryTheme(signalConfig.signalCategories, { prioritiesCopy: signalConfig.prioritiesCopy }),
    [signalConfig]
  );

  return (
    <SignalCategoriesProvider value={theme}>
      <Cards
        topicClusteringLens={topicClusteringLens}
        period={period}
        comparePeriod={comparePeriod}
        compareKey={compareKey}
        periodLoading={periodLoading}
        dateFieldMap={dateFieldMap}
        signalMetaById={signalMetaById}
        titleByContentType={titleByContentType}
        onCountsMeta={rememberCountsMeta}
        onAskAI={askAiAbout}
      />
    </SignalCategoriesProvider>
  );
};

// Inside the provider, so the scoring reads the tenant's categories.
const NO_FILTERS = [];
// Every level of the side-panel stack, the same width.
const PANEL_WIDTH = 440;
const Cards = ({ topicClusteringLens, period, comparePeriod, compareKey, periodLoading, dateFieldMap, signalMetaById, titleByContentType, onCountsMeta, onAskAI }) => {
  const { counts, scored, seriesByNode, signalSeries, periodKeys, grain, loading, signalsLoading } = usePrioritiesScoring({
    topicClusteringLens,
    historyFilters: NO_FILTERS,
    period,
    compareKey,
    dateFieldMap,
    signalMetaById,
    withSignalSeries: true,
    onCountsMeta,
  });
  // The side-panel stack over the section: a signal category's topic list
  // (from a category card), a topic's landing page (from that list, or
  // straight from a Topics-widget row), and a sub-topic's annotations. One
  // navigation state; changing a level drops the levels in front of it.
  // Each level owns its own requests; they share one scope — the section's
  // period, narrowed to the open category's signals when there is one.
  const [nav, setNav] = useState({ category: null, topicId: null, row: null });
  const openCategory = (category) => setNav({ category, topicId: null, row: null });
  const openTopic = (topicId) => setNav((n) => ({ ...n, topicId, row: null }));
  const openRow = (row) => setNav((n) => ({ ...n, row }));
  const categoryTheme = useSignalCategories();
  const panelScope = useMemo(
    () => ({
      period,
      comparePeriod,
      extraFilters: nav.category ? [signalsFilter(signalIdsInCategory(signalMetaById, nav.category))].filter(Boolean) : [],
      topicClusteringLens,
      dateFieldMap,
    }),
    [period, comparePeriod, nav.category, signalMetaById, topicClusteringLens, dateFieldMap]
  );
  const panelActiveFilters = useMemo(() => activeFiltersFor(panelScope.period, panelScope.extraFilters), [panelScope]);
  if (periodLoading || (period && loading)) return <SectionSkeleton />;
  // No dated observation on the tenant (nothing annotated yet).
  if (!period) {
    return (
      <DashboardWidgetShell title="Priorities">
        <UI_CAPTION style={{ color: colors2.grey60, padding: "12px 0" }}>No observations yet. Annotate some documents and the priorities appear here.</UI_CAPTION>
      </DashboardWidgetShell>
    );
  }
  // A first month with nothing before it: no line to draw over time and no
  // momentum to read, so the trend, the zones and the two zone lists sit
  // blurred behind one note saying what would fill them in. The category
  // cards and the topic shares stay as they are — their shares are this
  // month's, only their change column has nothing to compare with.
  // The one flag behind all of it (shared with the fires stat card). The
  // trend widget reads it too, for its lead-in months.
  const singlePeriod = isSinglePeriod(scored.meta);
  const copy = SM_UIContent.SM_Dashboard.singlePeriod;
  const noteFor = (text) => (singlePeriod ? { blurNote: text, blurNoteIcon: CalendarPlus } : {});
  const signalTrendNote = noteFor(copy.signalTrend);
  const zonesNote = noteFor(copy.zones);
  const firesNote = noteFor(copy.fires);
  const emergingNote = noteFor(copy.emerging);
  return (
    <Stack>
      {/* The category cards and the trend are the only two widgets that
          read the signal series. The counts and the sub-topic series they
          share with the Priorities page are already cached, so the rest
          paints immediately and those two fill in on their own. */}
      <Halves>
        {signalsLoading ? (
          <SignalCardsSkeleton />
        ) : (
          <SignalCategoryCards scored={scored} signalSeries={signalSeries} signalMetaById={signalMetaById} titleByContentType={titleByContentType} onOpenCategory={openCategory} />
        )}
        <TopicSharesWidget counts={counts} seriesByNode={seriesByNode} scored={scored} onOpenTopic={openTopic} />
      </Halves>
      <Halves>
        {signalsLoading ? (
          <SignalTrendSkeleton />
        ) : (
          <SignalCategoryTrend signalSeries={signalSeries} periodKeys={periodKeys} signalMetaById={signalMetaById} grain={grain} singlePeriod={singlePeriod} {...signalTrendNote} />
        )}
        <ZonesGridWidget items={scored.priorities} meta={scored.meta} {...zonesNote} />
      </Halves>
      <Halves>
        <ZoneSubtopicsWidget zone="fires" items={scored.priorities} meta={scored.meta} {...firesNote} />
        <ZoneSubtopicsWidget zone="emerging" items={scored.priorities} meta={scored.meta} {...emergingNote} />
      </Halves>
      <SidePanelStack
        levels={[
          {
            key: "category",
            isOpen: !!nav.category,
            width: PANEL_WIDTH,
            onClose: () => setNav({ category: null, topicId: null, row: null }),
            render: (shell) => (
              <CategoryTopicsPanel
                {...shell}
                isOpen={!!nav.category}
                width={PANEL_WIDTH}
                categoryDisplay={categoryTheme.list.find((c) => c.value === nav.category)?.display}
                scope={panelScope}
                titleByContentType={titleByContentType}
                onOpenTopic={openTopic}
              />
            ),
          },
          {
            key: "topic",
            isOpen: !!nav.topicId,
            width: PANEL_WIDTH,
            onClose: () => setNav((n) => ({ ...n, topicId: null, row: null })),
            render: (shell) => (
              <TopicDrilldownPanel
                {...shell}
                isOpen={!!nav.topicId}
                width={PANEL_WIDTH}
                topicNodeId={nav.topicId}
                scope={panelScope}
                titleByContentType={titleByContentType}
                openRowNodeId={nav.row ? String(nav.row.nodeId) : null}
                onOpenRow={openRow}
              />
            ),
          },
          {
            key: "row",
            isOpen: !!nav.row,
            width: PANEL_WIDTH,
            onClose: () => setNav((n) => ({ ...n, row: null })),
            render: (shell) => (
              <SubtopicRowPanel
                {...shell}
                row={nav.row}
                topicNodeId={nav.topicId}
                activeFilters={panelActiveFilters}
                topicClusteringLens={topicClusteringLens}
                signalMetaById={signalMetaById}
                period={period}
                dateFieldMap={dateFieldMap}
                onAskAI={onAskAI}
              />
            ),
          },
        ]}
      />
    </Stack>
  );
};
